import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { usePlateEditorState, focusEditor, isCollapsed, ELEMENT_LINK, usePlateEditorRef } from '@udecode/plate';

import {
  MARK_LINK_SELECTION,
  MARK_LINK_SELECTION_FIRST_NODE,
} from 'components/text_editor_new/plugins/inline_links/create_link_selection_plugin';
import OutsideClickHandler from 'components/common/utilities/outside_click_handler';
import { getNormalizedUrl } from 'components/lib/linkify_it';
import hasPluginOfType from 'components/text_editor_new/lib/has_plugin_of_type';
import { Item } from 'components/text_editor_new/components/controls/controls_new';
import { LinkEditor } from 'components/composer/shared/link_editor';
import { usePopoverMenu } from 'components/common/menu';

const DEFAULT_URL = '';

function BaseLinkButton({ className, hotKey }) {
  const { targetRef, setTargetRef, isOpen, onClose, onOpen } = usePopoverMenu();
  const [linkUrl, setLinkUrl] = useState(DEFAULT_URL);
  const [isEditing, setIsEditing] = useState(false);
  const buttonRef = useRef();

  const editor = usePlateEditorState();

  useEffect(() => {
    if (editor.registerHotkeyCallback && editor.cancelHotkeyCallback && hotKey) {
      editor.registerHotkeyCallback(hotKey, onKeyboardShortcut);

      return () => editor.cancelHotkeyCallback(hotKey);
    }
  });

  const cleanUp = useCallback(() => {
    if (isOpen) {
      setIsEditing(false);
      setLinkUrl(DEFAULT_URL);
      onClose();
      setTargetRef(buttonRef.current);

      editor.removeLinkSelectionMarks();
      focusEditor(editor, editor.selection);
    }
  }, [isOpen, onClose, setTargetRef, editor]);

  // By the time we are here, we should already have the "link selection" marks inserted. Try to get
  // the DOM node corresponding to the first mark, in case of error fall back onto the link button
  // itself just so we have some popup target for the editor to use.
  // We have to do the DOM querying "out-of-band" because slate-react runs the DOM mutations in a
  // semi-async fashion and by the time the marks are in the editor, the DOM is not yet updated
  const getPopupTargetAsync = () => {
    const query = `[data-${MARK_LINK_SELECTION}][data-${MARK_LINK_SELECTION_FIRST_NODE}]`;

    return new Promise(resolve => {
      setTimeout(() => {
        const domNode = document.query(query) || buttonRef.current;
        resolve(domNode);
      }, 0);
    });
  };

  const toggleWhenSelectionExists = () => {
    if (!isCollapsed(editor.selection)) {
      editor.setLinkSelectionMarks();
      if (editor.isLinkSelected()) {
        const [linkNode] = editor.getCurrentLinkNode();
        setIsEditing(true);
        setLinkUrl(linkNode.url);
      }

      getPopupTargetAsync().then(node => {
        setTargetRef(node);
        onOpen();
      });
    }
  };

  const onUpdateLink = newUrl => {
    editor.removeLinkSelectionMarks();
    const linkNode = editor.getCurrentLinkNode();
    editor.editLink(linkNode, getNormalizedUrl(newUrl));
    cleanUp();
  };

  const onCreateLink = newUrl => {
    editor.removeLinkSelectionMarks();
    editor.addLink(getNormalizedUrl(newUrl));
    cleanUp();
  };

  const onRemoveLink = () => {
    editor.removeLinkSelectionMarks();
    const linkNode = editor.getCurrentLinkNode();
    // Let normalizer handle removing the link node
    editor.editLink(linkNode, '');
    cleanUp();
  };

  const onKeyboardShortcut = (_, evt) => {
    evt.preventDefault();
    if (!isOpen) {
      toggleWhenSelectionExists();
    }
  };

  const onSubmit = isEditing ? onUpdateLink : onCreateLink;
  return (
    <OutsideClickHandler onClickOutside={cleanUp}>
      <Item
        className={className}
        data-aid="link"
        isDisabled={editor.shouldDisableLinkButton()}
        onClick={toggleWhenSelectionExists}
        ref={buttonRef}
      >
        <i className="fa fa-link" />
      </Item>
      <LinkEditor
        horizontalPosition="center"
        initialValue={linkUrl}
        isEditing={isEditing}
        isOpen={isOpen}
        onClose={cleanUp}
        onRemove={onRemoveLink}
        onSubmit={onSubmit}
        targetRef={targetRef}
        verticalPosition="top"
      />
    </OutsideClickHandler>
  );
}

BaseLinkButton.propTypes = {
  className: PropTypes.string,
  hotKey: PropTypes.string,
};

export function LinkButton(props) {
  const editor = usePlateEditorRef();
  return hasPluginOfType(editor, ELEMENT_LINK) ? <BaseLinkButton {...props} /> : null;
}
